/*! Burotic editorial article template. Vanilla JS, loaded only by sections/main-article-editorial.liquid */
(function () {
  'use strict';

  if (customElements.get('editorial-article')) return;

  var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var CALLOUTS = '.burotic-faq, .burotic-product-card, .burotic-promo, .burotic-pros-cons, .burotic-quick-answer, .burotic-key-takeaways, .burotic-tip, .burotic-warning, .burotic-checklist';

  function slugify(text) {
    var slug = (text || '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/['’]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 64);
    return slug || 'section';
  }

  function uniqueId(base, used) {
    var id = base;
    var i = 2;
    while (used.has(id) || document.getElementById(id)) {
      id = base + '-' + i;
      i += 1;
    }
    used.add(id);
    return id;
  }

  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (key) {
        if (attrs[key] === null || attrs[key] === undefined) return;
        node.setAttribute(key, attrs[key]);
      });
    }
    (children || []).forEach(function (child) {
      node.appendChild(typeof child === 'string' ? document.createTextNode(child) : child);
    });
    return node;
  }

  function stickyOffset() {
    var styles = getComputedStyle(document.documentElement);
    var sticky = parseFloat(styles.getPropertyValue('--sticky-header-height')) || 0;
    var header = parseFloat(styles.getPropertyValue('--header-height')) || 0;
    var headerEl = document.querySelector('header.header[is="sticky-header"]');
    var measured = headerEl ? headerEl.clientHeight : 0;
    return Math.max(sticky, header, measured);
  }

  class EditorialArticle extends HTMLElement {
    connectedCallback() {
      this.content = this.querySelector('[data-editorial-content]');
      if (!this.content) return;

      this.demoteContentH1();
      this.headings = this.prepareHeadings();
      this.buildToc();
      this.enhanceMedia();
      this.enhanceTables();
      this.enhanceFaq();
      this.placePromo();
      this.hydrateProductCards();
      this.setupProgress();
    }

    /* The page already has one H1 (the article title); H1s written in the body become H2s. */
    demoteContentH1() {
      this.content.querySelectorAll('h1').forEach(function (h1) {
        var h2 = document.createElement('h2');
        Array.prototype.forEach.call(h1.attributes, function (attr) { h2.setAttribute(attr.name, attr.value); });
        while (h1.firstChild) h2.appendChild(h1.firstChild);
        h1.replaceWith(h2);
      });
    }

    /* Unique, readable ids on H2/H3. Ids written by the author are kept. */
    prepareHeadings() {
      var used = new Set();
      var headings = Array.prototype.slice.call(this.content.querySelectorAll('h2, h3')).filter(function (heading) {
        return !heading.closest(CALLOUTS) && heading.textContent.trim().length > 0;
      });
      headings.forEach(function (heading) {
        if (heading.id) {
          used.add(heading.id);
        } else {
          heading.id = uniqueId(slugify(heading.textContent), used);
        }
      });
      return headings;
    }

    buildToc() {
      var minHeadings = parseInt(this.getAttribute('data-toc-min'), 10) || 3;
      var lists = Array.prototype.slice.call(this.querySelectorAll('[data-editorial-toc-list]'));
      if (!lists.length || this.headings.length < minHeadings) return;

      var self = this;
      var links = [];

      lists.forEach(function (list) {
        list.textContent = '';
        self.headings.forEach(function (heading) {
          var link = el('a', {
            href: '#' + heading.id,
            'class': 'editorial__toc-link editorial__toc-link--' + heading.tagName.toLowerCase(),
            'data-target': heading.id
          }, [heading.textContent.trim()]);
          link.addEventListener('click', function (event) {
            event.preventDefault();
            self.scrollToHeading(heading);
            var details = link.closest('details');
            if (details && details.open) details.removeAttribute('open');
          });
          links.push(link);
          list.appendChild(el('li', null, [link]));
        });
      });

      this.querySelectorAll('[data-editorial-toc], [data-editorial-toc-mobile]').forEach(function (node) {
        node.removeAttribute('hidden');
      });

      this.tocLinks = links;
      this.observeHeadings();
    }

    scrollToHeading(heading) {
      var top = heading.getBoundingClientRect().top + window.scrollY - stickyOffset() - 24;
      window.scrollTo({ top: Math.max(0, top), behavior: reducedMotion ? 'auto' : 'smooth' });
      if (history.replaceState) history.replaceState(null, '', '#' + heading.id);
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
    }

    observeHeadings() {
      if (!('IntersectionObserver' in window)) return;
      var self = this;
      var headings = this.headings;
      var visible = new Map();

      var setCurrent = function (id) {
        self.tocLinks.forEach(function (link) {
          if (link.getAttribute('data-target') === id) {
            link.setAttribute('aria-current', 'true');
          } else {
            link.removeAttribute('aria-current');
          }
        });
      };

      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          visible.set(entry.target.id, entry.isIntersecting);
        });
        var first = headings.find(function (heading) { return visible.get(heading.id); });
        if (first) {
          setCurrent(first.id);
          return;
        }
        var passed = null;
        var limit = stickyOffset() + 40;
        headings.forEach(function (heading) {
          if (heading.getBoundingClientRect().top < limit) passed = heading;
        });
        if (passed) setCurrent(passed.id);
      }, { rootMargin: '-' + Math.round(stickyOffset() + 32) + 'px 0px -55% 0px', threshold: [0, 1] });

      headings.forEach(function (heading) { observer.observe(heading); });
    }

    /* Images and embedded videos. */
    enhanceMedia() {
      var self = this;
      this.content.querySelectorAll('img').forEach(function (img, index) {
        if (!img.hasAttribute('loading') && index > 0) img.setAttribute('loading', 'lazy');
        if (!img.hasAttribute('decoding')) img.setAttribute('decoding', 'async');
      });

      this.content.querySelectorAll('iframe').forEach(function (iframe) {
        if (iframe.closest('.editorial__embed')) return;
        var src = iframe.getAttribute('src') || iframe.getAttribute('data-src') || '';
        var width = parseInt(iframe.getAttribute('width'), 10);
        var height = parseInt(iframe.getAttribute('height'), 10);

        var wrapper = el('div', { 'class': 'editorial__embed' });
        if (width && height) wrapper.style.setProperty('--embed-ratio', width + ' / ' + height);
        iframe.parentNode.insertBefore(wrapper, iframe);

        var youtubeId = self.youtubeId(src);
        if (!youtubeId) {
          iframe.setAttribute('loading', 'lazy');
          wrapper.appendChild(iframe);
          return;
        }

        var title = iframe.getAttribute('title') || '';
        var playLabel = self.getAttribute('data-play-label') || 'Play';
        var facade = el('button', {
          type: 'button',
          'class': 'editorial__embed-facade',
          'aria-label': title ? playLabel + ': ' + title : playLabel
        });
        var thumb = el('img', {
          src: 'https://i.ytimg.com/vi/' + youtubeId + '/hqdefault.jpg',
          alt: '',
          loading: 'lazy',
          decoding: 'async',
          width: '480',
          height: '360'
        });
        var play = el('span', { 'class': 'editorial__embed-play', 'aria-hidden': 'true' });
        play.innerHTML = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>';
        facade.appendChild(thumb);
        facade.appendChild(play);
        facade.addEventListener('click', function () {
          var url = new URL(src.replace(/^\/\//, 'https://'), window.location.href);
          url.searchParams.set('autoplay', '1');
          url.searchParams.set('rel', '0');
          iframe.setAttribute('src', url.toString());
          iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
          iframe.setAttribute('allowfullscreen', '');
          if (!iframe.getAttribute('title') && title) iframe.setAttribute('title', title);
          wrapper.appendChild(iframe);
          facade.remove();
          iframe.focus();
        });
        iframe.remove();
        wrapper.appendChild(facade);
      });
    }

    youtubeId(src) {
      if (!src) return null;
      var match = src.match(/(?:youtube(?:-nocookie)?\.com\/(?:embed\/|watch\?v=|v\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
      return match ? match[1] : null;
    }

    /* Tables get a scrollable wrapper so nothing overflows on small screens. */
    enhanceTables() {
      this.content.querySelectorAll('table').forEach(function (table) {
        if (table.closest('.editorial__table-wrap, .burotic-comparison')) return;
        var wrapper = el('div', { 'class': 'editorial__table-wrap', tabindex: '0', role: 'region' });
        var caption = table.querySelector('caption');
        if (caption) wrapper.setAttribute('aria-label', caption.textContent.trim());
        table.parentNode.insertBefore(wrapper, table);
        wrapper.appendChild(table);
      });
    }

    /* FAQ: native <details>, or H3/H4 questions followed by their answer. Empty items are removed. */
    enhanceFaq() {
      this.content.querySelectorAll('.burotic-faq').forEach(function (faq) {
        var current = null;
        if (faq.querySelector(':scope > h3, :scope > h4, :scope > h5, :scope > h6')) {
          Array.prototype.slice.call(faq.children).forEach(function (child) {
            if (child.tagName === 'DETAILS') {
              current = null;
            } else if (/^H[3-6]$/.test(child.tagName)) {
              current = el('details', { 'class': 'burotic-faq__item' }, [
                el('summary', null, [child.textContent.trim()]),
                el('div', { 'class': 'burotic-faq__answer' })
              ]);
              faq.insertBefore(current, child);
              child.remove();
            } else if (current) {
              current.lastElementChild.appendChild(child);
            }
          });
        }

        faq.querySelectorAll('details').forEach(function (details) {
          var summary = details.querySelector('summary');
          var hasAnswer = Array.prototype.some.call(details.children, function (child) {
            return child !== summary && child.textContent.trim().length > 0;
          });
          if (!summary || !summary.textContent.trim() || !hasAnswer) details.remove();
        });

        if (!faq.querySelector('details')) faq.remove();
      });
    }

    /* One promo per article: into the first .burotic-promo-slot, or after the Nth H2. */
    placePromo() {
      var holder = this.querySelector('[data-editorial-promo]');
      if (!holder) return;
      var promo = holder.firstElementChild;
      if (!promo) {
        holder.remove();
        return;
      }

      var slots = Array.prototype.slice.call(this.content.querySelectorAll('.burotic-promo-slot'));
      if (slots.length) {
        slots.slice(1).forEach(function (extra) { extra.remove(); });
        slots[0].textContent = '';
        slots[0].appendChild(promo);
      } else if (this.getAttribute('data-promo-auto') === 'true') {
        var after = parseInt(this.getAttribute('data-promo-after'), 10) || 2;
        var h2s = this.headings.filter(function (h) { return h.tagName === 'H2'; });
        if (h2s.length > after) {
          h2s[after].parentNode.insertBefore(promo, h2s[after]);
        } else {
          this.content.appendChild(promo);
        }
      }

      holder.remove();
    }

    /* Product cards: markup rendered by Shopify through the Section Rendering API. */
    hydrateProductCards() {
      var cards = this.content.querySelectorAll('.burotic-product-card[data-product-handle]');
      if (!cards.length) return;
      var root = this.getAttribute('data-root-url') || '/';
      var sectionId = this.getAttribute('data-product-section') || 'editorial-product-card';
      if (root.slice(-1) !== '/') root += '/';

      cards.forEach(function (card) {
        var handle = (card.getAttribute('data-product-handle') || '').trim();
        if (!handle) {
          card.setAttribute('data-state', 'fallback');
          return;
        }
        card.setAttribute('data-state', 'loading');
        var url = root + 'products/' + encodeURIComponent(handle) + '?section_id=' + encodeURIComponent(sectionId);

        fetch(url, { headers: { Accept: 'text/html' } })
          .then(function (response) {
            if (!response.ok) throw new Error('not-found');
            return response.text();
          })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var inner = doc.querySelector('[data-editorial-product]');
            if (!inner) throw new Error('empty');
            card.style.minHeight = card.getBoundingClientRect().height + 'px';
            card.textContent = '';
            card.appendChild(inner);
            card.setAttribute('data-state', 'ready');
            requestAnimationFrame(function () { card.style.minHeight = ''; });
          })
          .catch(function () {
            card.setAttribute('data-state', 'fallback');
          });
      });
    }

    /* Reading progress bar. */
    setupProgress() {
      var bar = this.querySelector('[data-editorial-progress]');
      if (!bar) return;
      var fill = bar.querySelector('[data-editorial-progress-bar]');
      var content = this.content;
      var ticking = false;

      var update = function () {
        ticking = false;
        var rect = content.getBoundingClientRect();
        var viewport = window.innerHeight;
        var total = rect.height - viewport * 0.5;
        var done = -rect.top + viewport * 0.5;
        var progress = total > 0 ? Math.min(1, Math.max(0, done / total)) : 0;
        fill.style.setProperty('--editorial-progress', progress.toFixed(4));
        bar.setAttribute('aria-valuenow', Math.round(progress * 100));
      };

      var onScroll = function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      update();
    }
  }

  customElements.define('editorial-article', EditorialArticle);
})();
